/**
 * LMS Course Authoring — course list (TRUS-225).
 *
 * Staff-facing management for the standardized Google Classroom-style LMS.
 * Lists all courses for the active org (including drafts), create / publish /
 * delete, and links into the per-course editor. Workforce is the umbrella;
 * this is the classroom-course authoring surface.
 *
 * Auth: staff JWT via authFetch (sends X-CBO-ID). Admin endpoints also take an
 * explicit cbo_id query param (active_cbo_id) and require ops_admin+.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  GraduationCap,
  Plus,
  BookOpen,
  Users,
  Eye,
  EyeOff,
  Trash2,
  Pencil,
  X,
  Loader2,
  BarChart2,
  Copy,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

interface AdminCourse {
  id: number;
  title: string;
  description: string | null;
  is_published: boolean;
  module_count: number;
  lesson_count: number;
  enrollment_count: number;
  workforce_curriculum_id: number | null;
}

const activeCboId = () => localStorage.getItem('active_cbo_id') || '';

export default function LmsCoursesAdminPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<AdminCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const cbo = activeCboId();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/lms/admin/courses?cbo_id=${cbo}`));
      if (!res.ok) throw new Error(res.status === 403 ? 'You need admin access to manage courses.' : 'Failed to load courses');
      setCourses(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [cbo]);

  useEffect(() => {
    load();
  }, [load]);

  const createCourse = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/lms/admin/courses?cbo_id=${cbo}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), description: newDesc.trim() || null, is_published: false }),
      });
      if (!res.ok) throw new Error('Could not create course');
      const created = await res.json();
      setShowCreate(false);
      setNewTitle('');
      setNewDesc('');
      navigate(`/workforce/courses/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Could not create course');
    } finally {
      setCreating(false);
    }
  };

  const togglePublish = async (course: AdminCourse) => {
    setBusyId(course.id);
    try {
      const res = await authFetch(
        getWeaveinApiUrl(`/api/lms/admin/courses/${course.id}/publish?published=${!course.is_published}`),
        { method: 'PATCH' }
      );
      if (!res.ok) throw new Error('Could not update publish state');
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not update publish state');
    } finally {
      setBusyId(null);
    }
  };

  const duplicateCourse = async (course: AdminCourse) => {
    setBusyId(course.id);
    setError('');
    try {
      const res = await authFetch(
        getWeaveinApiUrl(`/api/lms/admin/courses/${course.id}/duplicate?cbo_id=${cbo}`),
        { method: 'POST' },
      );
      if (!res.ok) throw new Error('Could not duplicate course');
      const created = await res.json();
      navigate(`/workforce/courses/${created.id}`); // open the draft copy for editing
    } catch (err: any) {
      setError(err.message || 'Could not duplicate course');
    } finally {
      setBusyId(null);
    }
  };

  const deleteCourse = async (course: AdminCourse) => {
    if (!confirm(`Delete "${course.title}"? This removes all its modules, lessons, and enrollments.`)) return;
    setBusyId(course.id);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/lms/admin/courses/${course.id}?cbo_id=${cbo}`), {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Could not delete course');
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not delete course');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Training"
        title="Classroom Courses"
        description="Author Google Classroom-style courses for members. Workforce curricula link into these."
        actions={
          <Button onClick={() => setShowCreate(true)} className="gap-2 shrink-0">
            <Plus className="w-4 h-4" />
            New course
          </Button>
        }
      />
      <PageContainer>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <Card className="mb-6 border-teal-200 dark:border-teal-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">New course</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="Course title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} autoFocus />
              <Textarea
                placeholder="Short description (optional)"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={2}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={createCourse} disabled={creating || !newTitle.trim()} className="gap-1">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create &amp; edit
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List */}
        {loading ? (
          <PageState variant="loading" title="Loading courses…" />
        ) : courses.length === 0 ? (
          <PageState
            variant="empty"
            title="No courses yet."
            action={
              <Button onClick={() => setShowCreate(true)} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" /> Create your first course
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4">
            {courses.map((course) => (
              <Card key={course.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-base">{course.title}</CardTitle>
                        {course.is_published ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300">
                            Published
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                        {course.workforce_curriculum_id && (
                          <Badge variant="outline" className="text-xs">Linked to curriculum #{course.workforce_curriculum_id}</Badge>
                        )}
                      </div>
                      {course.description && (
                        <CardDescription className="text-sm line-clamp-2 mt-1">{course.description}</CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4 text-xs text-ink-500">
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {course.module_count} modules</span>
                      <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" /> {course.lesson_count} lessons</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {course.enrollment_count} enrolled</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate(`/workforce/courses/${course.id}`)}>
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1" disabled={busyId === course.id} onClick={() => duplicateCourse(course)}>
                        <Copy className="w-3.5 h-3.5" /> Duplicate
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate(`/workforce/courses/${course.id}/insights`)}>
                        <BarChart2 className="w-3.5 h-3.5" /> Insights
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        disabled={busyId === course.id}
                        onClick={() => togglePublish(course)}
                      >
                        {course.is_published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {course.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        disabled={busyId === course.id}
                        onClick={() => deleteCourse(course)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>
    </div>
  );
}
